<template>
  <v-card flat>
    <v-card-text
      v-if="chart_type"
      :id="`${chart_type}_duvalChartDiv`"
      class="py-0"
      :ref="`${chart_type}_duvalChartDiv`"
    >
      <canvas :id="`${chart_type}_canvas`" width="650" height="500"></canvas>
    </v-card-text>
  </v-card>
</template>

<script>
// https://www.researchgate.net/publication/4345236_A_Software_Implementation_of_the_Duval_Triangle_Method

var v0 = { x: 114, y: 366 };
var v1 = { x: 306, y: 30 };
var v2 = { x: 498, y: 366 };
var triangle = [v0, v1, v2];

const DEF_CANVAS_WIDTH = 650;
const DEF_ASPECT_RATIO = 650 / 500;
const DEF_CHART_TYPE = "T4";

// label styles
var labelfontsize = 12;
var labelfontface = "verdana";
var labelpadding = 3;

// pre-create a canvas-image of the arrowhead
var arrowheadLength = 10;
var arrowheadWidth = 8;
var arrowhead;

export default {
  name: "DuvalChartVT",
  props: {
    chartData: Array,
    colors: Object,
    canvasWidth: String,
    gasData: Array,
    chartType: String,
  },
  watch: {
    gasData() {
      if (this.canvas) {
        const context = this.canvas.getContext("2d");
        context.clearRect(0, 0, this.canvas.width, this.canvas.height);
        this.initialize();
      }
    },
    "$vuetify.theme.dark"(val) {
      const palette = this.$vuetify.theme.themes[val ? "dark" : "light"];
      this.labelColor = palette.titleLight;
      this.initialize();
    },
  },
  data: function () {
    return {
      chartsLib: {},
      ctx: null,
      canvas: null,
      canvas_width: this.canvasWidth || DEF_CANVAS_WIDTH,
      chart_type: this.chartType || DEF_CHART_TYPE,
      scale: 1,
      translatedPostion: { x: 0, y: 0 },
      chartOptions: {
        outlineColor: "red",
        width: "100%",
      },
      labelColor: "black",
      /*
      Duval Triangle Definitions
      Segments can be defined by absolute points or by region points, where the region is specified in gas readings
      */
      duval_definitions: {
        T1: {
          legends: [
            "PD = Partial Discharge",
            "T1 = Thermal fault < 300 C",
            "T2 = Thermal fault 300 - 700 C",
            "T3 = Thermal fault > 700 C",
            "D1 = Low energy discharge (sparking)",
            "D2 = High energy discharge (arcing)",
            "DT = Mix of thermal and electrical faults",
          ],
          labels: ["% CH₄", "% C₂H₄", "% C₂H₂"],
          // segments defined here by absolute points (from https://stackoverflow.com/questions/36456577/how-to-create-duval-triangle-in-canvas)
          // for region points see here: https://powertransformerhealth.com/2019/03/22/duvals-triangle-method/
          segments: [
            {
              points: [
                { x: 114, y: 366 },
                { x: 281, y: 76 },
                { x: 324, y: 150 },
                { x: 201, y: 366 },
              ],
              fill: this.colors["D1"] || "rgb(172,236,222)",
              label: { text: "D1", cx: 200, cy: 290, withLine: false, endX: null, endY: null },
              path: null,
            },
            {
              points: [
                { x: 385, y: 366 },
                { x: 201, y: 366 },
                { x: 324, y: 150 },
                { x: 356, y: 204 },
                { x: 321, y: 256 },
              ],
              fill: this.colors["D2"] || "deepskyblue",
              label: { text: "D2", cx: 290, cy: 290, withLine: false, endX: null, endY: null },
              path: null,
            },
            {
              points: [
                { x: 297, y: 46 },
                { x: 392, y: 214 },
                { x: 372, y: 248 },
                { x: 441, y: 366 },
                { x: 385, y: 366 },
                { x: 321, y: 256 },
                { x: 356, y: 204 },
                { x: 281, y: 76 },
              ],
              fill: this.colors["DT"] || "lightCyan",
              label: { text: "DT", cx: 370, cy: 290, withLine: false, endX: 366, endY: 120 },
              path: null,
            },
            {
              points: [
                { x: 306, y: 30 },
                { x: 312, y: 40 },
                { x: 300, y: 40 },
              ],
              fill: this.colors["PD"] || "black",
              label: { text: "PD", cx: 356, cy: 40, withLine: true, endX: 321, endY: 40 },
              path: null,
            },
            {
              points: [
                { x: 312, y: 40 },
                { x: 348, y: 103 },
                { x: 337, y: 115 },
                { x: 297, y: 46 },
                { x: 300, y: 40 },
              ],
              fill: this.colors["T1"] || "navajoWhite",
              label: { text: "T1", cx: 375, cy: 70, withLine: true, endX: 340, endY: 75 },
              path: null,
            },
            {
              points: [
                { x: 348, y: 103 },
                { x: 402, y: 199 },
                { x: 392, y: 214 },
                { x: 337, y: 115 },
              ],
              fill: this.colors["T2"] || "tan",
              label: { text: "T2", cx: 400, cy: 125, withLine: true, endX: 366, endY: 120 },
              path: null,
            },
            {
              points: [
                { x: 402, y: 199 },
                { x: 498, y: 366 },
                { x: 441, y: 366 },
                { x: 372, y: 248 },
              ],
              fill: this.colors["T3"] || "peru",
              label: { text: "T3", cx: 425, cy: 290, withLine: false, endX: null, endY: null },
              path: null,
            },
          ],
        },

        T4: {
          legends: [
            "PD = Partial Discharge",
            "S = Stray gassing at < 200 C",
            "C = Possible paper carbonization",
            "O = Overheating < 250 C without carbonization",
            "ND = Not Defined",
          ],
          labels: ["% H₂", "% CH₄", "% C₂H₆"],
          // segments defined here by region points
          // see https://powertransformerhealth.com/2022/07/27/duvals-triangle-4
          // for more information
          segments: [
            {
              region_points: [
                [64, 36, 0],
                [40, 36, 24],
                [15, 61, 24],
                [15, 55, 30],
                [9, 61, 30],
                [9, 45, 46],
                [54, 0, 46],
                [100, 0, 0],
              ],
              fill: this.colors["S"] || "peru",
              label: { text: "S", cx: 300, cy: 170, withLine: false, endX: null, endY: null },
              path: null,
            },
            {
              region_points: [
                [98, 2, 0],
                [97, 2, 1],
                [84, 15, 1],
                [85, 15, 0],
              ],
              fill: this.colors["PD"] || "black",
              label: { text: "PD", cx: 356, cy: 60, withLine: true, endX: 330, endY: 40 },
              path: null,
            },
            {
              region_points: [
                [64, 36, 0],
                [40, 36, 24],
                [15, 61, 24],
                [15, 55, 30],
                [9, 61, 30],
                [9, 61, 30],
                [0, 70, 30],
                [0, 100, 0],
              ],
              fill: this.colors["C"] || "lightCyan",
              label: { text: "C", cx: 400, cy: 270, withLine: false, endX: null, endY: null },
              path: null,
            },
            {
              region_points: [
                [9, 61, 30],
                [0, 70, 30],
                [0, 0, 100],
                [9, 0, 91],
              ],
              fill: this.colors["O"] || "tan",
              label: { text: "O", cx: 240, cy: 352, withLine: false, endX: null, endY: null },
              path: null,
            },
            {
              region_points: [
                [9, 45, 46],
                [9, 0, 91],
                [54, 0, 46],
              ],
              fill: this.colors["ND"] || "navajoWhite",
              label: { text: "ND", cx: 220, cy: 300, withLine: false, endX: null, endY: null },
              path: null,
            },
          ],
        },
        T5: {
          legends: [
            "PD = Partial Discharge",
            "S = Stray gassing at < 200 C",
            "ND = Not Defined",
            "C = Possible paper carbonization",
            "O = Overheating < 250 C (no carbonization)",
            "T2-H = Thermal fault T2 in mineral liquid only",
            "T3-H = Thermal fault T3 in mineral liquid only",
          ],
          labels: ["% CH4", "% C2H4", "% C2H6"],
          // segments defined here by region points
          // see https://powertransformerhealth.com/2022/07/28/duvals-triangle-5/
          // for more information
          segments: [
            {
              region_points: [
                [46, 0, 54],
                [36, 10, 54],
                [75, 10, 15],
                [85, 0, 15],
              ],
              fill: this.colors["S"] || "peru",
              label: { text: "S", cx: 245, cy: 170, withLine: false, endX: null, endY: null },
              path: null,
            },
            {
              region_points: [
                [85, 0, 15],
                [75, 10, 15],
                [90, 10, 0],
                [100, 0, 0],
              ],
              fill: this.colors["O"] || "tan",
              label: { text: "O", cx: 305, cy: 72, withLine: false, endX: null, endY: null },
              path: null,
            },
            {
              region_points: [
                [0, 0, 100],
                [0, 10, 90],
                [36, 10, 54],
                [46, 0, 54],
              ],
              fill: this.colors["O"] || "tan",
              label: { text: "O", cx: 182, cy: 282, withLine: false, endX: null, endY: null },
              path: null,
            },
            {
              region_points: [
                [85, 0, 15],
                [84, 1, 15],
                [97, 1, 2],
                [98, 0, 2],
              ],
              fill: this.colors["PD"] || "black",
              label: { text: "PD", cx: 256, cy: 50, withLine: true, endX: 286, endY: 40 },
              path: null,
            },
            {
              region_points: [
                [60, 10, 30],
                [0, 70, 30],
                [16, 70, 14],
                [38, 48, 14],
                [40, 48, 12],
                [78, 10, 12],
              ],
              fill: this.colors["C"] || "lightCyan",
              label: { text: "C", cx: 330, cy: 220, withLine: false, endX: null, endY: null },
              path: null,
            },

            {
              region_points: [
                [0, 10, 90],
                [60, 10, 30],
                [35, 35, 30],
                [0, 35, 65],
              ],
              fill: this.colors["ND"] || "navajoWhite",
              label: { text: "ND", cx: 230, cy: 300, withLine: false, endX: null, endY: null },
              path: null,
            },
            {
              region_points: [
                [78, 10, 12],
                [53, 35, 12],
                [65, 35, 0],
                [90, 10, 0],
              ],
              fill: this.colors["T2-H"] || "DeepSkyBlue",
              label: { text: "T2", cx: 340, cy: 130, withLine: false, endX: null, endY: null },
              path: null,
            },
            {
              region_points: [
                [65, 35, 0],
                [53, 35, 12],
                [40, 48, 12],
                [38, 48, 14],
                [16, 70, 14],
                [0, 70, 30],
                [0, 100, 0],
              ],
              fill: this.colors["T3-H"] || "LightPink",
              label: { text: "T3", cx: 410, cy: 250, withLine: false, endX: null, endY: null },
              path: null,
            },
            {
              region_points: [
                [0, 35, 65],
                [35, 35, 30],
                [0, 70, 30],
              ],
              fill: this.colors["T3-H"] || "LightPink",
              label: { text: "T3", cx: 315, cy: 315, withLine: false, endX: null, endY: null },
              path: null,
            },
          ],
        },
      },
    };
  },
  methods: {
    /*
     *  Create the Duval Triangle
     */
    createDuval() {
      this.scale = this.canvas_width / DEF_CANVAS_WIDTH;
      if (this.scale) {
        this.ctx.scale(this.scale, this.scale);
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      }

      // draw colored segments inside triangle
      for (var i = 0; i < this.duval_definitions[this.chart_type].segments.length; i++) {
        this.drawSegment(this.duval_definitions[this.chart_type].segments[i]);
      }
      // draw ticklines
      this.ticklines(v0, v1, 9, 0, 15);
      this.ticklines(v1, v2, 9, (Math.PI * 3) / 4, 15);
      this.ticklines(v2, v0, 9, (Math.PI * 5) / 4, 15);
      // molecules
      this.moleculeLabel(v0, v1, 100, Math.PI, this.duval_definitions[this.chart_type].labels[0]);
      this.moleculeLabel(v1, v2, 100, 0, this.duval_definitions[this.chart_type].labels[1]);
      this.moleculeLabel(v2, v0, 75, Math.PI / 2, this.duval_definitions[this.chart_type].labels[2]);
      // draw outer triangle
      this.drawTriangle(triangle);
      // draw legend
      this.drawLegend(this.duval_definitions[this.chart_type].legends, 10, 10, 12.86);
    },

    /**
     *
     * @param {Number} unit_x
     * @param {Number} unit_y
     *
     * @returns {Object} {x: Number, y: Number}
     *
     * Translate the unit coordinates to canvas coordinates
     */
    translatePoint(unit_x, unit_y) {
      var max_x = v2.x - v0.x;
      var max_y = v0.y - v1.y;
      var new_x = Math.round(unit_x * max_x + v0.x);
      var new_y = Math.round(max_y - unit_y * max_y);
      return { x: new_x, y: new_y };
    },

    /**
     *
     * @param {Event} event
     *
     * Get the mouse position
     *
     */
    getMousePosition(event) {
      let rect = this.canvas.getBoundingClientRect();
      let x = event.clientX - rect.left;
      let y = event.clientY - rect.top;
      console.log("Coordinate x: " + x, "Coordinate y: " + y);
    },

    // end drawing
    /////////////////////

    /**
     *
     * @param {Number} g1
     * @param {Number} g2
     * @param {Number} g3
     *
     * Translate the gas readings to a point
     *
     * @returns {Object} {x: Number, y: Number}
     *
     */
    translateReadingsToPoint(g1, g2, g3) {
      //Ry = By + RG = By + P1 * L * cos 30°
      //Rx = Bx + BG = Bx + L * (P2 + P1 * 0.5)

      var gtot = g1 + g2 + g3;
      var p1 = g1 / gtot;
      var p2 = g2 / gtot;

      var By = v0.y;
      var Bx = v0.x;
      var L = v2.x - v0.x;

      var angle30_rad = 30 * (Math.PI / 180);
      var Rx = Bx + L * (p2 + p1 * 0.5);
      var Ry = By - p1 * L * Math.cos(angle30_rad);
      return { x: Math.floor(Rx), y: Math.floor(Ry) };
    },

    /**
     *
     * @param {Number} g1
     * @param {Number} g2
     * @param {Number} g3
     *
     * Draw the transformer position
     *
     */
    drawTransformerPosition(g1, g2, g3) {
      var point = this.translateReadingsToPoint(g1, g2, g3);
      var radius = 5; // or whatever
      var fillColor = "#0000ff";
      this.translatedPostion.x = point.x;
      this.translatedPostion.y = point.y;
      this.ctx.fillStyle = fillColor;
      this.ctx.beginPath();
      this.ctx.moveTo(point.x, point.y);
      this.ctx.arc(point.x, point.y, radius, 0, Math.PI * 2, false);
      this.ctx.fill();
    },

    premakeArrowhead() {
      var actx = arrowhead.getContext("2d");
      arrowhead.width = arrowheadLength;
      arrowhead.height = arrowheadWidth;
      actx.beginPath();
      actx.moveTo(0, 0);
      actx.lineTo(arrowheadLength, arrowheadWidth / 2);
      actx.lineTo(0, arrowheadWidth);
      actx.closePath();
      actx.fillStyle = this.labelColor;
      actx.fill();
    },

    /**
     *
     * @param {Object} s
     *
     * Draw the segment
     *
     */
    drawSegment(s) {
      // draw and fill the segment path
      var region_translate = false;
      var translated = null;
      if (s.region_points != null) {
        region_translate = true;
      }
      s.path = new Path2D();
      var x;
      var y;
      if (!region_translate) {
        x = s.points[0].x;
        y = s.points[0].y;
      } else {
        translated = this.translateReadingsToPoint(s.region_points[0][0], s.region_points[0][1], s.region_points[0][2]);
        //console.log("Untranslated: " + s.region_points[0][0] + "," + s.region_points[0][1]  + ", " + s.region_points[0][2]);
        //console.log("Translated: " + translated.x, translated.y);
        x = translated.x;
        y = translated.y;
      }
      s.path.moveTo(x, y);
      var points = region_translate ? s.region_points : s.points;
      for (var i = 1; i < points.length; i++) {
        if (!region_translate) {
          x = points[i].x;
          y = points[i].y;
        } else {
          translated = this.translateReadingsToPoint(points[i][0], points[i][1], points[i][2]);
          //console.log("Untranslated: " + points[i][0] + "," + points[i][1]  + ", " + points[i][2]);
          //console.log("Translated: " + translated.x, translated.y);
          x = translated.x;
          y = translated.y;
        }
        s.path.lineTo(x, y);
      }
      s.path.closePath();
      this.ctx.fillStyle = s.fill;
      this.ctx.fill(s.path);

      this.ctx.lineWidth = 1;
      this.ctx.strokeStyle = "black";
      this.ctx.stroke(s.path);
      // draw segment's box label
      if (s.label.withLine) {
        this.lineBoxedLabel(s, labelfontsize, labelfontface, labelpadding);
      } else {
        this.boxedLabel(s, labelfontsize, labelfontface, labelpadding);
      }
    },
    moleculeLabel(start, end, offsetLength, angle, text) {
      this.ctx.textAlign = "center";
      this.ctx.textBaseline = "middle";
      this.ctx.font = "14px verdana";
      var dx = end.x - start.x;
      var dy = end.y - start.y;
      var x0 = parseInt(start.x + dx * 0.5);
      var y0 = parseInt(start.y + dy * 0.5);
      var x1 = parseInt(x0 + offsetLength * Math.cos(angle));
      var y1 = parseInt(y0 + offsetLength * Math.sin(angle));
      this.ctx.fillStyle = this.labelColor;
      this.ctx.fillText(text, x1, y1);
      // arrow
      x0 = parseInt(start.x + dx * 0.35);
      y0 = parseInt(start.y + dy * 0.35);
      x1 = parseInt(x0 + 50 * Math.cos(angle));
      y1 = parseInt(y0 + 50 * Math.sin(angle));
      var x2 = parseInt(start.x + dx * 0.65);
      var y2 = parseInt(start.y + dy * 0.65);
      var x3 = parseInt(x2 + 50 * Math.cos(angle));
      var y3 = parseInt(y2 + 50 * Math.sin(angle));
      this.ctx.beginPath();
      this.ctx.moveTo(x1, y1);
      this.ctx.lineTo(x3, y3);
      this.ctx.strokeStyle = "black";
      this.ctx.lineWidth = 1;
      this.ctx.stroke();
      angle = Math.atan2(dy, dx);
      this.ctx.translate(x3, y3);
      this.ctx.rotate(angle);
      this.ctx.drawImage(arrowhead, -arrowheadLength, -arrowheadWidth / 2);
      this.ctx.setTransform(this.scale, 0, 0, this.scale, 0, 0);
    },

    boxedLabel(s, fontsize, fontface, padding) {
      var centerX = s.label.cx;
      var centerY = s.label.cy;
      var text = s.label.text;
      this.ctx.textAlign = "center";
      this.ctx.textBaseline = "middle";
      this.ctx.font = fontsize + "px " + fontface;
      var textwidth = this.ctx.measureText(text).width;
      var textheight = fontsize * 1.286;
      var leftX = centerX - textwidth / 2 - padding;
      var topY = centerY - textheight / 2 - padding;
      this.ctx.fillStyle = "white";
      this.ctx.fillRect(leftX, topY, textwidth + padding * 2, textheight + padding * 2);
      this.ctx.lineWidth = 1;
      this.ctx.strokeRect(leftX, topY, textwidth + padding * 2, textheight + padding * 2);
      this.ctx.fillStyle = "black";
      this.ctx.fillText(text, centerX, centerY);
    },

    lineBoxedLabel(s, fontsize, fontface, padding) {
      var centerX = s.label.cx;
      var centerY = s.label.cy;
      var text = s.label.text;
      var lineToX = s.label.endX;

      this.ctx.textAlign = "center";
      this.ctx.textBaseline = "middle";
      this.ctx.font = fontsize + "px " + fontface;
      //set font color
      var textwidth = this.ctx.measureText(text).width;
      var textheight = fontsize * 1.286;
      var leftX = centerX - textwidth / 2 - padding;
      var topY = centerY - textheight / 2 - padding;
      // the line
      this.ctx.beginPath();
      this.ctx.moveTo(leftX, topY + textheight / 2);
      this.ctx.lineTo(lineToX, topY + textheight / 2);
      this.ctx.strokeStyle = "black";
      this.ctx.lineWidth = 1;
      this.ctx.stroke();
      // the boxed text
      this.ctx.fillStyle = "white";
      this.ctx.fillRect(leftX, topY, textwidth + padding * 2, textheight + padding * 2);
      this.ctx.strokeRect(leftX, topY, textwidth + padding * 2, textheight + padding * 2);
      this.ctx.fillStyle = "black";
      this.ctx.fillText(text, centerX, centerY);
    },

    ticklines(start, end, count, angle, length) {
      var dx = end.x - start.x;
      var dy = end.y - start.y;
      this.ctx.lineWidth = 1;
      for (var i = 1; i < count; i++) {
        var x0 = parseInt(start.x + (dx * i) / count);
        var y0 = parseInt(start.y + (dy * i) / count);
        var x1 = parseInt(x0 + length * Math.cos(angle));
        var y1 = parseInt(y0 + length * Math.sin(angle));
        this.ctx.beginPath();
        this.ctx.moveTo(x0, y0);
        this.ctx.lineTo(x1, y1);
        this.ctx.stroke();
        if (i == 2 || i == 4 || i == 6 || i == 8) {
          var labelOffset = (length * 3) / 4;
          x1 = parseInt(x0 - labelOffset * Math.cos(angle));
          y1 = parseInt(y0 - labelOffset * Math.sin(angle));
          this.ctx.fillStyle = this.labelColor;
          this.ctx.fillText(parseInt(i * 10), x1, y1);
        }
      }
    },

    drawTriangle(t) {
      this.ctx.beginPath();
      this.ctx.moveTo(t[0].x, t[0].y);
      this.ctx.lineTo(t[1].x, t[1].y);
      this.ctx.lineTo(t[2].x, t[2].y);
      this.ctx.closePath();
      this.ctx.strokeStyle = "black";
      this.ctx.lineWidth = 2;
      this.ctx.stroke();
    },

    drawLegend(texts, x, y, lineheight) {
      this.ctx.textAlign = "left";
      this.ctx.textBaseline = "top";
      this.ctx.fillStyle = this.labelColor;
      this.ctx.font = "12px arial";
      for (var i = 0; i < texts.length; i++) {
        this.ctx.fillText(texts[i], x, y + i * lineheight);
      }
    },
    initialize() {
      const palette = this.$vuetify.theme.themes[this.$vuetify.theme.dark ? "dark" : "light"];
      this.labelColor = palette.titleLight;
      // start drawing
      this.canvas = document.getElementById(this.chart_type + "_canvas");
      this.canvas.width = this.canvas_width;
      this.canvas.height = this.canvas_width / DEF_ASPECT_RATIO;
      this.ctx = this.canvas.getContext("2d");

      arrowhead = document.createElement("canvas");
      this.premakeArrowhead();
      this.createDuval();
      if (this.gasData) {
        this.drawTransformerPosition(this.gasData[0], this.gasData[1], this.gasData[2]);
      }
      this.$emit(this.chart_type, this.problem);
    },
  },

  computed: {
    problem() {
      var fault_val = "OK";
      var segments = this.duval_definitions[this.chart_type].segments;
      for (let i = 0; i < segments.length; i++) {
        if (
          this.ctx.isPointInPath(
            segments[i].path,
            this.translatedPostion.x * this.scale,
            this.translatedPostion.y * this.scale
          )
        ) {
          fault_val = segments[i].label.text;
        }
      }
      return fault_val;
    },
  },
  created() {},
  mounted() {
    this.initialize();
  },
};
</script>
<style lang="scss" scoped>
body {
  background-color: ivory;
  padding: 10px;
}

#canvas {
  margin: 0 auto;
}
</style>
